<template>
  <div class="pfin">
    <!-- Primeira carga: esqueleto no formato da pagina. Trocar o mes ou
         atualizar mantem a pagina e mostra o carregamento so na tabela. -->
    <page-skeleton v-if="!loaded" variant="finance" class="mt-4" />

    <template v-else>
      <!-- herói: quanto entra + período -->
      <div class="pfin-hero">
        <div class="pfin-hero__intro">
          <p class="pfin__eyebrow">{{ $t("partnerFinance.eyebrow") }}</p>
          <span class="pfin-tag">{{ $t("partnerFinance.toReceive") }}</span>
          <p class="pfin-money pfin-display">
            <span class="pfin-money__sym">R$</span
            ><span class="pfin-money__int">{{
              moneyParts(summary.totalPendingAmount).int
            }}</span
            ><span class="pfin-money__dec"
              >{{ $t("formats.decimalSep")
              }}{{ moneyParts(summary.totalPendingAmount).dec }}</span
            >
          </p>
          <p class="pfin-hero__meta">
            {{
              $tc("partnerFinance.pendingMeta", summary.pendingCount || 0, {
                count: summary.pendingCount || 0,
                period: periodLabel,
              })
            }}
          </p>
        </div>

        <!-- conta de recebimento: sem ela, não há repasse -->
        <div
          class="pfin-bank"
          :class="recipientReady ? 'pfin-bank--ok' : 'pfin-bank--pending'"
        >
          <div class="pfin-bank__row">
            <span class="pfin-bank__status">
              <i
                class="mdi"
                :class="
                  recipientReady ? 'mdi-check-circle' : 'mdi-alert-circle'
                "
              ></i>
              {{
                recipientReady
                  ? $t("partnerFinance.accountActive")
                  : $t("partnerFinance.accountPending")
              }}
            </span>
          </div>
          <p class="pfin-bank__text">
            {{
              recipientReady
                ? $t("partnerFinance.readyText")
                : $t("partnerFinance.pendingText")
            }}
          </p>
          <button class="pfin-bank__btn" type="button" @click="openBankDialog">
            {{
              recipientReady
                ? $t("partnerFinance.editAccount")
                : $t("partnerFinance.addAccount")
            }}
          </button>
        </div>
      </div>

      <!-- filtros -->
      <div class="pfin-toolbar">
        <v-select
          v-model="filters.month"
          :items="months"
          item-text="label"
          item-value="value"
          :label="$t('partnerFinance.month')"
          outlined
          dense
          hide-details
          class="pfin-field"
          @change="loadFinance"
        />
        <v-text-field
          v-model.number="filters.year"
          :label="$t('partnerFinance.year')"
          type="number"
          outlined
          dense
          hide-details
          class="pfin-field pfin-field--year"
          @change="loadFinance"
        />
        <v-select
          v-model="filters.status"
          :items="statusFilterOptions"
          item-text="text"
          item-value="value"
          :label="$t('partnerFinance.status')"
          outlined
          dense
          clearable
          hide-details
          class="pfin-field"
        />
        <v-text-field
          v-model="filters.search"
          :label="$t('partnerFinance.search')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          class="pfin-field pfin-field--grow"
        />
        <v-btn
          color="secondary-500"
          dark
          depressed
          rounded
          class="pfin-refresh"
          :loading="loading"
          @click="loadFinance"
        >
          {{ $t("partnerFinance.refresh") }}
        </v-btn>
      </div>

      <!-- composição do mês -->
      <div class="pfin-card pfin-bar-card">
        <div class="pfin-bar-head">
          <div>
            <p class="pfin-section">{{ $t("partnerFinance.composition") }}</p>
            <p class="pfin-section__sub">
              {{ $t("partnerFinance.compositionSub") }}
            </p>
          </div>
        </div>

        <div
          v-if="moneyBar.total > 0"
          class="pfin-bar"
          role="img"
          :aria-label="barAria"
        >
          <span
            v-for="seg in moneyBar.seg"
            :key="seg.key"
            class="pfin-bar__seg"
            :style="{ width: seg.pct + '%', background: seg.color }"
          ></span>
        </div>
        <div v-else class="pfin-bar pfin-bar--empty"></div>

        <div class="pfin-bar-legend">
          <div v-for="seg in moneyBar.seg" :key="seg.key" class="pfin-leg">
            <span
              class="pfin-leg__dot"
              :style="{ background: seg.color }"
            ></span>
            <div class="pfin-leg__body">
              <p class="pfin-leg__label">{{ seg.label }}</p>
              <p class="pfin-leg__value">
                {{ formatCurrencyBR(seg.amount) }}
                <span class="pfin-leg__count">· {{ seg.count }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- tabela de mensalidades -->
      <div class="pfin-card pfin-table">
        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="loading"
          loading-text=""
          item-key="id"
          :no-data-text="$t('partnerFinance.noCharges')"
        >
          <template #loading>
            <page-skeleton variant="rows" :rows="4" />
          </template>
          <template v-slot:[`item.amount`]="{ item }">
            <div class="pfin-cell-amount">
              {{ formatCurrencyBR(item.amount) }}
            </div>
            <span v-if="item.isManualAmount" class="pfin-tagline">{{
              $t("partnerFinance.adjusted")
            }}</span>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <span
              class="pfin-status"
              :style="{ '--c': chargeStatusColor(item.status) }"
            >
              {{ chargeStatusLabel(item.status) }}
            </span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom :disabled="canEditCharge(item)">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    small
                    text
                    color="secondary-500"
                    :disabled="!canEditCharge(item)"
                    @click="openAdjustmentDialog(item)"
                  >
                    {{ $t("partnerFinance.change") }}
                  </v-btn>
                </span>
              </template>
              <span>{{ disabledChargeReason(item) }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </div>

      <!-- como funciona (contexto, discreto) -->
      <div class="pfin-note">
        <i class="mdi mdi-information-outline"></i>
        <p>
          {{ $t("partnerFinance.note") }}
        </p>
      </div>
    </template>

    <v-dialog v-model="adjustmentDialog.open" max-width="520">
      <v-card class="pfin-dialog">
        <v-card-title>{{ $t("partnerFinance.adjustTitle") }}</v-card-title>
        <v-card-text v-if="adjustmentDialog.charge">
          <div class="mb-4">
            <strong>{{ adjustmentDialog.charge.customerName }}</strong>
            <div class="pfin-muted">
              {{ adjustmentDialog.charge.classCode }} ·
              {{ adjustmentDialog.charge.referenceLabel }} ·
              {{ formatCurrencyBR(adjustmentDialog.charge.amount) }}
            </div>
          </div>

          <div class="pfin-label mb-2">
            {{ $t("partnerFinance.adjustValue") }}
          </div>
          <!-- Card 17trt6je251 (retorno de 22/10): mês fechado trava o
               valor; o status continua editável. -->
          <p
            v-if="isPastReference(adjustmentDialog.charge)"
            class="pfin-muted mb-2"
          >
            {{ $t("partnerFinance.pastValueLocked") }}
          </p>
          <v-radio-group
            v-model="adjustmentDialog.mode"
            row
            class="mt-0"
            :disabled="isPastReference(adjustmentDialog.charge)"
          >
            <v-radio :label="$t('partnerFinance.fixed')" value="FIXED" />
            <v-radio
              :label="$t('partnerFinance.percentage')"
              value="PERCENTAGE"
            />
          </v-radio-group>
          <v-text-field
            v-if="adjustmentDialog.mode === 'FIXED'"
            v-model.number="adjustmentDialog.amount"
            :disabled="isPastReference(adjustmentDialog.charge)"
            :label="$t('partnerFinance.newAmount')"
            prefix="R$"
            type="number"
            min="0.01"
            outlined
            dense
            :hint="$t('partnerFinance.newAmountHint')"
            persistent-hint
          />
          <v-text-field
            v-else
            v-model.number="adjustmentDialog.percentage"
            :disabled="isPastReference(adjustmentDialog.charge)"
            :label="$t('partnerFinance.percentLabel')"
            suffix="%"
            type="number"
            outlined
            dense
            :hint="$t('partnerFinance.percentHint')"
            persistent-hint
          />

          <v-divider class="my-4" />

          <div class="pfin-label mb-3">
            {{ $t("partnerFinance.changeStatus") }}
          </div>
          <v-select
            v-model="adjustmentDialog.status"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            :label="$t('partnerFinance.chargeStatus')"
            outlined
            dense
            hide-details
          >
            <template v-slot:item="{ item }">
              <v-chip
                small
                :color="chargeStatusColor(item.value)"
                dark
                class="mr-2"
              >
                {{ item.text }}
              </v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip small :color="chargeStatusColor(item.value)" dark>
                {{ item.text }}
              </v-chip>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="adjustmentDialog.open = false">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn
            color="secondary-500"
            dark
            depressed
            :loading="adjustmentDialog.saving"
            @click="saveAdjustment"
          >
            {{ $t("common.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bankDialog.open" max-width="560" persistent>
      <v-card class="pfin-dialog">
        <v-card-title>{{ $t("partnerFinance.bankTitle") }}</v-card-title>
        <v-card-text>
          <v-form ref="bankForm">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="bankDialog.form.bankCode"
                  :label="$t('partnerFinance.bankCode')"
                  :hint="$t('partnerFinance.bankCodeHint')"
                  persistent-hint
                  maxlength="3"
                  :rules="[rules.required, rules.bankCode]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="bankDialog.form.bankAccountType"
                  :items="accountTypeOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('partnerFinance.accountType')"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="8" md="9">
                <v-text-field
                  v-model.trim="bankDialog.form.bankBranchNumber"
                  :label="$t('partnerFinance.branch')"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4" md="3">
                <v-text-field
                  v-model.trim="bankDialog.form.bankBranchCheckDigit"
                  :label="$t('partnerFinance.branchDigit')"
                  maxlength="2"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="8" md="9">
                <v-text-field
                  v-model.trim="bankDialog.form.bankAccountNumber"
                  :label="$t('partnerFinance.account')"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4" md="3">
                <v-text-field
                  v-model.trim="bankDialog.form.bankAccountCheckDigit"
                  :label="$t('partnerFinance.accountDigit')"
                  maxlength="2"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-alert type="info" dense text class="mb-0 mt-1">
              {{ $t("partnerFinance.ownAccount") }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bankDialog.open = false">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn
            color="secondary-500"
            dark
            depressed
            :loading="bankDialog.saving"
            @click="saveBankAccount"
          >
            {{ $t("common.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import { formatBRL, languageTag } from "@/plugins/i18n";

// Rótulos dos status da cobrança (chaves de partnerFinance).
const STATUS_KEYS = {
  DRAFT: "partnerFinance.draft",
  PENDING: "partnerFinance.pending",
  OVERDUE: "partnerFinance.overdue",
  PAID: "partnerFinance.paid",
};

export default {
  name: "PartnerFinance",

  data() {
    const today = new Date();
    return {
      loading: false,
      // A primeira carga terminou (com ou sem erro).
      loaded: false,
      filters: {
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        status: null,
        search: "",
      },
      items: [],
      summary: {},
      adjustmentDialog: {
        open: false,
        saving: false,
        charge: null,
        mode: "FIXED",
        amount: null,
        percentage: null,
        status: null,
      },
      recipient: { status: null, ready: false },
      bankDialog: {
        open: false,
        saving: false,
        form: {
          bankCode: "",
          bankBranchNumber: "",
          bankBranchCheckDigit: "",
          bankAccountNumber: "",
          bankAccountCheckDigit: "",
          bankAccountType: "checking",
        },
      },
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          this.$t("common.requiredFieldDot"),
        bankCode: (v) =>
          /^\d{3}$/.test(v || "") || this.$t("partnerFinance.threeDigits"),
      },
      snackbar: { show: false, text: "", color: "success" },
    };
  },

  computed: {
    months() {
      return Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        label: this.$t(`enums.month.${index + 1}`),
      }));
    },
    headers() {
      return [
        { text: this.$t("partnerFinance.colStudent"), value: "customerName" },
        { text: this.$t("partnerFinance.colClass"), value: "classCode" },
        {
          text: this.$t("partnerFinance.colReference"),
          value: "referenceLabel",
        },
        { text: this.$t("partnerFinance.colDue"), value: "dueDateLabel" },
        { text: this.$t("partnerFinance.colStatus"), value: "status" },
        { text: this.$t("partnerFinance.colAmount"), value: "amount" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
    statusOptions() {
      return ["PENDING", "OVERDUE", "PAID"].map((value) => ({
        value,
        text: this.$t(STATUS_KEYS[value]),
      }));
    },
    // Opcoes do filtro da listagem (inclui Rascunho, que so o professor ve).
    statusFilterOptions() {
      return ["DRAFT", "PENDING", "OVERDUE", "PAID"].map((value) => ({
        value,
        text: this.$t(STATUS_KEYS[value]),
      }));
    },
    accountTypeOptions() {
      return [
        { value: "checking", text: this.$t("partnerFinance.checking") },
        { value: "savings", text: this.$t("partnerFinance.savings") },
      ];
    },
    recipientReady() {
      return Boolean(this.recipient && this.recipient.ready);
    },
    filteredItems() {
      const term = (this.filters.search || "").trim().toLowerCase();
      const status = this.filters.status;
      return this.items.filter((item) => {
        const matchesStatus = !status || item.status === status;
        const matchesTerm =
          !term ||
          [item.customerName, item.classCode]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(term));
        return matchesStatus && matchesTerm;
      });
    },
    periodLabel() {
      const month = this.months.find((m) => m.value === this.filters.month);
      return `${month ? month.label : ""} ${this.filters.year}`.trim();
    },
    moneyBar() {
      const paid = Number(this.summary.totalPaidAmount || 0);
      const pending = Number(this.summary.totalPendingAmount || 0);
      const overdue = Number(this.summary.totalOverdueAmount || 0);
      const total = paid + pending + overdue;
      const pct = (v) => (total > 0 ? (v / total) * 100 : 0);
      return {
        total,
        seg: [
          {
            key: "paid",
            label: this.$t("partnerFinance.received"),
            amount: paid,
            count: this.summary.paidCount || 0,
            color: "#00a379",
            pct: pct(paid),
          },
          {
            key: "pending",
            label: this.$t("partnerFinance.receivable"),
            amount: pending,
            count: this.summary.pendingCount || 0,
            color: "#ff5d2a",
            pct: pct(pending),
          },
          {
            key: "overdue",
            label: this.$t("partnerFinance.late"),
            amount: overdue,
            count: this.summary.overdueCount || 0,
            color: "#d52323",
            pct: pct(overdue),
          },
        ],
      };
    },
    barAria() {
      return this.moneyBar.seg
        .map((s) => `${s.label}: ${this.formatCurrencyBR(s.amount)}`)
        .join(", ");
    },
  },

  mounted() {
    this.loadFinance();
  },

  methods: {
    async loadFinance() {
      this.loading = true;
      try {
        const response = await partnerApi.getPartnerFinance({
          month: this.filters.month,
          year: this.filters.year,
        });
        const data = response.data || {};
        this.items = data.items || [];
        this.summary = data.summary || {};
        this.recipient = data.recipient || { status: null, ready: false };
      } catch (error) {
        this.items = [];
        this.summary = {};
        this.showMessageFromError(error, this.$t("partnerFinance.loadFailed"));
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },

    openBankDialog() {
      if (this.$refs.bankForm) this.$refs.bankForm.resetValidation();
      this.bankDialog.open = true;
    },

    async saveBankAccount() {
      if (this.$refs.bankForm && !this.$refs.bankForm.validate()) return;

      this.bankDialog.saving = true;
      try {
        const form = this.bankDialog.form;
        const payload = {
          bankCode: form.bankCode,
          bankBranchNumber: form.bankBranchNumber,
          bankAccountNumber: form.bankAccountNumber,
          bankAccountCheckDigit: form.bankAccountCheckDigit,
          bankAccountType: form.bankAccountType,
        };
        if (form.bankBranchCheckDigit) {
          payload.bankBranchCheckDigit = form.bankBranchCheckDigit;
        }

        const response = await partnerApi.updateBankAccount(payload);
        const result = response.data || {};
        this.recipient = {
          status: result.recipientStatus || null,
          ready: Boolean(result.gatewayRecipientId),
        };

        this.bankDialog.open = false;
        if (this.recipient.ready) {
          this.showMessage(this.$t("partnerFinance.bankSaved"), "success");
        } else {
          this.showMessage(this.$t("partnerFinance.bankPartial"), "error");
        }
      } catch (error) {
        this.showMessageFromError(error, this.$t("partnerFinance.bankFailed"));
      } finally {
        this.bankDialog.saving = false;
      }
    },

    openAdjustmentDialog(charge) {
      this.adjustmentDialog = {
        open: true,
        saving: false,
        charge,
        mode: "FIXED",
        amount: charge.amount,
        percentage: null,
        status: charge.status,
      };
    },

    async saveAdjustment() {
      const { charge, mode, amount, percentage, status } =
        this.adjustmentDialog;
      if (!charge) return;

      const hasValueChange =
        !this.isPastReference(charge) &&
        (mode === "FIXED"
          ? amount !== null && Number(amount) !== Number(charge.amount)
          : percentage !== null &&
            percentage !== undefined &&
            percentage !== "");

      const hasStatusChange = status && status !== charge.status;

      if (!hasValueChange && !hasStatusChange) {
        this.adjustmentDialog.open = false;
        return;
      }

      this.adjustmentDialog.saving = true;
      try {
        if (hasValueChange) {
          const payload =
            mode === "FIXED"
              ? { mode: "FIXED", amount: Number(amount) }
              : { mode: "PERCENTAGE", percentage: Number(percentage) };
          await partnerApi.updateGroupMonthlyCharge(
            charge.classId,
            charge.id,
            payload
          );
        }

        if (hasStatusChange) {
          await partnerApi.updateGroupChargeStatus(
            charge.classId,
            charge.id,
            status
          );
        }

        this.adjustmentDialog.open = false;
        await this.loadFinance();
        this.showMessage(this.$t("partnerFinance.updated"), "success");
      } catch (error) {
        this.showMessageFromError(error, this.$t("partnerFinance.saveFailed"));
      } finally {
        this.adjustmentDialog.saving = false;
      }
    },

    // Mês de referência anterior ao atual: o valor não muda (mesma regra da
    // API, assertAmountEditable).
    isPastReference(item) {
      if (!item || !item.month || !item.year) return false;
      const now = new Date();
      const reference = Number(item.year) * 12 + Number(item.month);
      const current = now.getFullYear() * 12 + now.getMonth() + 1;
      return reference < current;
    },

    canEditCharge(item) {
      if (!item) return false;
      return item.status !== "PAID";
    },

    disabledChargeReason(item) {
      if (!item) return "";
      if (item.status === "PAID") return this.$t("partnerFinance.paidLocked");
      return "";
    },

    chargeStatusLabel(status) {
      return STATUS_KEYS[status] ? this.$t(STATUS_KEYS[status]) : status;
    },

    chargeStatusColor(status) {
      const colors = {
        DRAFT: "#a826e1",
        PENDING: "#ff5d2a",
        OVERDUE: "#d52323",
        PAID: "#00a379",
      };
      return colors[status] || "#6b6472";
    },

    formatCurrencyBR(value) {
      return formatBRL(value);
    },

    moneyParts(value) {
      const abs = Math.abs(Number(value || 0)).toFixed(2);
      const [int, dec] = abs.split(".");
      return {
        int: Number(int).toLocaleString(languageTag()),
        dec,
      };
    },

    showMessage(text, color) {
      this.snackbar = { show: true, text, color };
    },

    showMessageFromError(error, fallback) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
          ? error.response.data.message
          : fallback;
      this.showMessage(message, "error");
    },
  },
};
</script>

<style scoped>
.pfin {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --grape: var(--dk-grape, #a826e1);
  width: 100%;
  padding: 8px 4px 40px;
  color: var(--ink);
  font-family: "Poppins", sans-serif;
}

.pfin-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}
.pfin__eyebrow {
  margin: 0 0 12px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--grape);
}
.pfin-tag {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

/* herói */
.pfin-hero {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 18px;
  align-items: stretch;
  margin-top: 8px;
}
.pfin-hero__intro {
  padding: 26px 28px;
  border-radius: 24px;
  background: radial-gradient(
      120% 140% at 100% 0%,
      rgba(168, 38, 225, 0.1),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.pfin-money {
  margin: 8px 0 0;
  color: var(--ink);
  line-height: 1;
  font-weight: 700;
}
.pfin-money__sym {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--muted);
  margin-right: 6px;
  vertical-align: 0.5em;
}
.pfin-money__int {
  font-size: clamp(2.6rem, 6vw, 3.6rem);
}
.pfin-money__dec {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--muted);
}
.pfin-hero__meta {
  margin: 12px 0 0;
  font-size: 0.9rem;
  color: var(--muted);
}

/* conta bancária */
.pfin-bank {
  min-width: 260px;
  max-width: 320px;
  padding: 20px 22px;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.pfin-bank--ok {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pfin-bank--pending {
  background: linear-gradient(
    158deg,
    #ff7d55 0%,
    var(--dk-ember, #e85526) 100%
  );
  color: #fff;
  box-shadow: 0 20px 38px -22px rgba(232, 85, 38, 0.55);
}
.pfin-bank__row {
  display: flex;
  align-items: center;
}
.pfin-bank__status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.82rem;
  font-weight: 700;
}
.pfin-bank--ok .pfin-bank__status {
  color: var(--dk-teal, #00a379);
}
.pfin-bank__status .mdi {
  font-size: 18px;
}
.pfin-bank__text {
  margin: 8px 0 14px;
  font-size: 0.82rem;
  line-height: 1.5;
}
.pfin-bank--ok .pfin-bank__text {
  color: var(--muted);
}
.pfin-bank--pending .pfin-bank__text {
  color: rgba(255, 255, 255, 0.9);
}
.pfin-bank__btn {
  align-self: flex-start;
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}
.pfin-bank--ok .pfin-bank__btn {
  background: var(--grape);
  color: #fff;
}
.pfin-bank--pending .pfin-bank__btn {
  background: var(--dk-surface, #fff);
  color: var(--dk-ember, #e85526);
}
.pfin-bank__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -12px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pfin-bank__btn:focus-visible {
  outline: 2px solid var(--dk-surface, #fff);
  outline-offset: 2px;
}

/* card base */
.pfin-card {
  margin-top: 18px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pfin-section {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.pfin-section__sub {
  margin: 3px 0 0;
  font-size: 0.85rem;
  color: var(--muted);
}

/* filtros */
.pfin-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
}
.pfin-field {
  max-width: 180px;
}
.pfin-field--year {
  max-width: 110px;
}
.pfin-field--grow {
  flex: 1 1 240px;
  max-width: none;
}
.pfin-refresh.v-btn {
  height: 40px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  margin-left: auto;
}

/* barra de composição */
.pfin-bar-card {
  padding: 22px 24px;
}
.pfin-bar-head {
  margin-bottom: 16px;
}
.pfin-bar {
  display: flex;
  height: 16px;
  border-radius: 999px;
  overflow: hidden;
  background: #f1eef4;
}
.pfin-bar__seg {
  height: 100%;
  transition: width 0.4s ease;
}
.pfin-bar__seg + .pfin-bar__seg {
  box-shadow: -1px 0 0 rgba(255, 255, 255, 0.9);
}
.pfin-bar--empty {
  background: repeating-linear-gradient(
    90deg,
    #ece9f0 0 10px,
    transparent 10px 18px
  );
}
.pfin-bar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  margin-top: 18px;
}
.pfin-leg {
  display: flex;
  align-items: flex-start;
  gap: 9px;
}
.pfin-leg__dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-top: 4px;
}
.pfin-leg__label {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.pfin-leg__value {
  margin: 2px 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.pfin-leg__count {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--muted);
}

/* tabela */
.pfin-table {
  overflow: hidden;
}
.pfin-cell-amount {
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.pfin-tagline {
  display: inline-block;
  margin-top: 2px;
  font-size: 0.66rem;
  font-weight: 600;
  color: var(--grape);
  letter-spacing: 0.04em;
}
.pfin-status {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--c);
  background: color-mix(in srgb, var(--c) 12%, var(--dk-surface, #fff));
  white-space: nowrap;
}
.pfin-table ::v-deep .v-data-table {
  background: transparent;
  border-radius: 20px;
}
.pfin-table ::v-deep .v-data-table__wrapper th {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 11px !important;
  font-weight: 700;
  color: var(--muted) !important;
  white-space: nowrap;
}
.pfin-table ::v-deep .v-data-table__wrapper td {
  font-size: 0.88rem;
  color: var(--ink);
}

/* nota */
.pfin-note {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #f8f6fb;
  border: 1px solid var(--line);
  color: var(--muted);
  font-size: 0.82rem;
  line-height: 1.55;
}
.pfin-note .mdi {
  font-size: 20px;
  color: var(--grape);
  flex: none;
  margin-top: 1px;
}
.pfin-note p {
  margin: 0;
}

/* diálogos */
.pfin-dialog {
  border-radius: 20px;
}
.pfin-muted {
  color: var(--muted);
  font-size: 0.85rem;
}
.pfin-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

@media (max-width: 860px) {
  .pfin-hero {
    grid-template-columns: 1fr;
  }
  .pfin-bank {
    min-width: 0;
    max-width: none;
  }
  .pfin-refresh.v-btn {
    margin-left: 0;
  }
}
</style>
